import {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import {
  ArrowLeft,
  Check,
  Code2,
  Copy,
  Download,
  Eye,
  EyeOff,
  Globe,
  History,
  Undo2,
  Redo2,
  ImagePlus,
  Link2,
  LockKeyhole,
  MapPin,
  RefreshCw,
  Save,
  Settings2,
  Trash2,
  Upload,
} from "lucide-react";
import type {
  AdminMap,
  MapCategory,
  MapDocument,
  MapLocation,
  MapStand,
} from "../shared/types";
import { validateMapDocument } from "../shared/validation";
import { EventMap, type EventMapHandle } from "../map/EventMap";
import { LocationPicker } from "./LocationPicker";
import { PublicPreviewLink } from "./PublicPreviewLink";
import { useVenueMapLogos } from "../map/useVenueMapLogos";
import type { MapViewState } from "../shared/mapControls";
import { LocationFields } from "./LocationFields";
import { RoutingEditor } from "./RoutingEditor";
import "./editing-confidence.css";
import { AddPlace, RemovePlace, AddBoothLocation } from "./PlaceAuthoring";
import { useDraftHistory } from "./useDraftHistory";
import { MapChangeReview, MapHistoryDialog } from "./MapReviewDialogs";
import { DeleteMapDialog } from "./DeleteMapDialog";
import type { MapComparison, MapSourcePreview } from "../shared/history";
import { compareMapDocuments } from "../shared/mapComparison";
import { mapStandHeight } from "../map/venueMapProjection";
import { api, ApiError, errorText, logoFileType } from "./api";
import { download, Notice, PageHeading, Status, updatedDate } from "./ui";
import "./booth-logos.css";
import "./source-import.css";

const categories: { value: MapCategory; label: string }[] = [
  { value: "exhibitor", label: "Exhibitor" },
  { value: "stage", label: "Stage" },
  { value: "lounge", label: "Lounge" },
  { value: "facility", label: "Facility" },
  { value: "unassigned", label: "Unassigned" },
];
async function validateLogoImage(file: File): Promise<void> {
  const source = URL.createObjectURL(file);
  const image = new Image();
  try {
    image.src = source;
    try {
      await image.decode();
    } catch {
      throw new Error("This image could not be opened. Choose another logo.");
    }
    if (
      !image.naturalWidth ||
      !image.naturalHeight ||
      image.naturalWidth > 4096 ||
      image.naturalHeight > 4096 ||
      image.naturalWidth * image.naturalHeight > 8_000_000
    )
      throw new Error(
        "Choose a logo no larger than 4,096 pixels per side and 8 million pixels total.",
      );
  } finally {
    URL.revokeObjectURL(source);
  }
}
function splitName(name: string): string[] {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  for (const word of words) {
    const last = lines.length - 1;
    if (last >= 0 && `${lines[last]} ${word}`.length <= 20)
      lines[last] += ` ${word}`;
    else lines.push(word);
  }
  return lines;
}
function updateStandName(
  document: MapDocument,
  stand: MapStand,
  name: string,
): MapDocument {
  const labels = { ...document.labels.stands };
  delete labels[stand.id];
  const lines = splitName(name);
  const firstY = stand.label.nameLines[0]?.y ?? 4;
  return {
    ...document,
    labels: { ...document.labels, stands: labels },
    geometry: {
      ...document.geometry,
      stands: document.geometry.stands.map((item) =>
        item.id === stand.id
          ? {
              ...item,
              name,
              shortName: name,
              label: {
                ...item.label,
                nameLines: lines.map((text, index) => ({
                  text,
                  y: firstY + index * item.label.nameFontSize * 1.15,
                })),
              },
            }
          : item,
      ),
    },
    locations: document.locations.map((location) =>
      location.id === stand.id &&
      (location.label === stand.id ||
        location.label === `${stand.id} · ${stand.name}`)
        ? { ...location, label: name ? `${stand.id} · ${name}` : stand.id }
        : location,
    ),
  };
}

export function MapEditor({
  initial,
  canEdit,
  canDelete,
  onBack,
  onChange,
  onDeleted,
  onDirtyChange,
}: {
  initial: AdminMap;
  canEdit: boolean;
  canDelete: boolean;
  onBack: () => void;
  onChange: (map: AdminMap) => void;
  onDeleted: (map: AdminMap) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [map, setMap] = useState(initial);
  const draftHistory = useDraftHistory(
    initial.document,
    initial.revision,
    canEdit ? api.identity() : "",
  );
  const { draft } = draftHistory;
  // Hidden booths still need an accurate directory preview. Keep this display
  // document separate from the draft and share one logo batch with the map.
  const logoDocument = useMemo(
    () => ({
      ...draft,
      geometry: {
        ...draft.geometry,
        stands: draft.geometry.stands.map((item) =>
          item.hidden ? { ...item, hidden: false } : item,
        ),
      },
    }),
    [draft],
  );
  const logos = useVenueMapLogos(
    logoDocument,
    `/api/admin/maps/${encodeURIComponent(draft.id)}/logos`,
  );
  const [tab, setTab] = useState<"details" | "locations" | "embed">("details");
  const [selectedStand, setSelectedStand] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const routingOpener = useRef<HTMLButtonElement>(null);
  const reviewOpener = useRef<HTMLElement | null>(null);
  const historyOpener = useRef<HTMLElement | null>(null);
  const [busy, setBusy] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const deleteOpener = useRef<HTMLButtonElement>(null);
  const deletePending = useRef(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deleteStale, setDeleteStale] = useState(false);
  const [routingOpen, setRoutingOpen] = useState(false);
  const [review, setReview] = useState<{
    kind: "source" | "publish" | "recovery";
    comparison: MapComparison;
    source?: MapSourcePreview;
  } | null>(null);
  const [reviewError, setReviewError] = useState("");
  const draftLocked = !canEdit || !!busy || !!draftHistory.recovery;
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [notice, setNotice] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const previewHandle = useRef<EventMapHandle | null>(null);
  const editorMounted = useRef(true);
  const previewToken = useRef<string | null>(null);
  useLayoutEffect(() => {
    editorMounted.current = true;
    return () => {
      editorMounted.current = false;
      const token = previewToken.current;
      previewToken.current = null;
      if (token)
        void api.cancelSource(initial.id, token).catch(() => undefined);
    };
  }, [initial.id]);
  const latestView = useRef<MapViewState>({
    zoom: 1,
    ...initial.document.camera,
  });
  const [previewReady, setPreviewReady] = useState(false);
  const rememberView = useCallback((view: MapViewState) => {
    latestView.current = view;
  }, []);
  const readyPreview = useCallback((handle: EventMapHandle) => {
    previewHandle.current = handle;
    setPreviewReady(true);
  }, []);
  const file = useRef<HTMLInputElement>(null);
  const logoFile = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState("");
  const [logoDragging, setLogoDragging] = useState(false);
  const logoDragDepth = useRef(0);
  const logoUploadPending = useRef(false);
  const saveStatusId = useId();
  const saveValidationId = `${saveStatusId}-validation`;
  const saveButton = useRef<HTMLButtonElement>(null);
  const savePending = useRef(false);
  useLayoutEffect(() => {
    const saveShortcut = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.shiftKey ||
        event.key.toLowerCase() !== "s"
      )
        return;
      // Own Save while this editor is open, even when there is nothing to save.
      // Clicking the current button uses the latest draft and disabled state
      // and shares the normal save/error workflow without focusing the button.
      event.preventDefault();
      if (!event.repeat) saveButton.current?.click();
    };
    window.addEventListener("keydown", saveShortcut);
    return () => window.removeEventListener("keydown", saveShortcut);
  }, []);
  const baseline = useMemo(() => JSON.stringify(map.document), [map.document]);
  const dirty = JSON.stringify(draft) !== baseline;
  useLayoutEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useLayoutEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (
        event.defaultPrevented ||
        event.isComposing ||
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        draftLocked ||
        document.querySelector("dialog[open]") ||
        target?.closest("input, textarea, [contenteditable=true]")
      )
        return;
      const key = event.key.toLowerCase();
      if (key !== "z" && !(event.ctrlKey && key === "y")) return;
      event.preventDefault();
      if (event.repeat) return;
      if (event.shiftKey || key === "y") draftHistory.redo();
      else draftHistory.undo();
      setNotice("");
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
  }, [draftHistory, draftLocked]);
  const stand = draft.geometry.stands.find((item) => item.id === selectedStand);
  const location = draft.locations.find((item) => item.id === selectedLocation);
  const standIds = useMemo(
    () => new Set(draft.geometry.stands.map((item) => item.id)),
    [draft.geometry.stands],
  );
  const places = draft.locations.filter((item) => !standIds.has(item.id));
  const place = location && !standIds.has(location.id) ? location : undefined;
  const standLocation = draft.locations.find((item) => item.id === stand?.id);
  const pins = useMemo(
    () =>
      location
        ? [
            {
              id: "admin-preview",
              locationId: location.id,
              label: location.label,
            },
          ]
        : [],
    [location],
  );
  function change(next: MapDocument) {
    if (!canEdit || draftHistory.recovery || deletePending.current) return;
    draftHistory.change(next);
    onDirtyChange(JSON.stringify(next) !== baseline);
    setNotice("");
  }
  function adopt(next: AdminMap, history: "reset" | "preserve" = "reset") {
    setMap(next);
    if (history === "preserve")
      draftHistory.markSaved(next.document, next.revision);
    else draftHistory.reset(next.document, next.revision);
    onDirtyChange(false);
    onChange(next);
    setConflict(false);
    setDeleteStale(false);
    if (selectedStand) {
      if (
        !next.document.geometry.stands.some((item) => item.id === selectedStand)
      ) {
        clearSelection();
      } else {
        setSelectedLocation(
          next.document.locations.some((item) => item.id === selectedStand)
            ? selectedStand
            : "",
        );
      }
    } else if (
      !next.document.locations.some((item) => item.id === selectedLocation)
    ) {
      clearSelection();
    }
  }
  async function action(
    kind: "save" | "publish" | "unpublish" | "reload" | "refresh",
    confirmed = false,
  ) {
    if (draftHistory.recovery || busy || deletePending.current || deleteOpen)
      return;
    if ((kind === "publish" || kind === "refresh") && !confirmed) {
      if (!canEdit || dirty || (kind === "refresh" && !map.source)) return;
      setBusy(kind === "refresh" ? "refresh" : "review");
      setError("");
      setReviewError("");
      try {
        if (kind === "refresh") {
          const source = await api.previewSource(map);
          if (!editorMounted.current) {
            void api.cancelSource(map.id, source.token).catch(() => undefined);
            return;
          }
          previewToken.current = source.token;
          setReview({ kind: "source", comparison: source.comparison, source });
        } else {
          const result = await api.publishReview(map);
          if (result.revision !== map.revision)
            throw new ApiError(
              "This map has changed since you opened it. Load the latest saved map before reviewing publication.",
              "revision_conflict",
              409,
            );
          setReview({ kind: "publish", comparison: result.comparison });
        }
      } catch (error) {
        setError(errorText(error));
        setConflict(
          error instanceof ApiError &&
            (error.status === 409 || error.status === 412),
        );
      } finally {
        setBusy("");
      }
      return;
    }
    if (kind === "save") {
      if (
        !canEdit ||
        !dirty ||
        busy ||
        savePending.current ||
        logoUploadPending.current ||
        !draft.title.trim()
      )
        return;
      // A second keypress can arrive before the disabled button is rendered.
      savePending.current = true;
    }
    if (kind === "refresh" && (!canEdit || busy || dirty || !map.source))
      return;
    if (
      kind === "reload" &&
      dirty &&
      !window.confirm(
        "Discard your unsaved changes and load the latest saved map?",
      )
    )
      return;
    if (
      kind === "unpublish" &&
      !window.confirm(
        "Unpublish this map? Existing embeds will no longer be able to load it.",
      )
    )
      return;
    setBusy(kind);
    setError("");
    setNotice("");
    try {
      const next =
        kind === "save"
          ? await api.save(draft, map.revision)
          : kind === "reload"
            ? await api.map(map.id)
            : kind === "refresh"
              ? await api.applySource(map, review?.source?.token || "")
              : await api.publish(map, kind === "publish");
      adopt(
        next,
        kind === "save" || kind === "publish" || kind === "unpublish"
          ? "preserve"
          : "reset",
      );
      previewToken.current = null;
      setReview(null);
      setReviewError("");
      setNotice(
        kind === "save"
          ? "Draft saved. Publish when you’re ready to update your embeds."
          : kind === "publish"
            ? "Map published. Embeds will receive the new version shortly."
            : kind === "unpublish"
              ? "Map unpublished. Your draft is still here."
              : kind === "refresh"
                ? "Source refreshed into your saved draft. Review the map before publishing; your current embeds are unchanged."
                : "Latest saved map loaded.",
      );
    } catch (error) {
      if (review) setReviewError(errorText(error));
      if (
        !review ||
        (error instanceof ApiError &&
          (error.status === 409 || error.status === 412))
      )
        setError(errorText(error));
      setConflict(
        error instanceof ApiError &&
          (error.status === 409 || error.status === 412),
      );
    } finally {
      if (kind === "save") savePending.current = false;
      setBusy("");
    }
  }
  async function removeMap() {
    if (
      !canDelete ||
      !deleteOpen ||
      deleteStale ||
      busy ||
      deletePending.current ||
      savePending.current ||
      logoUploadPending.current
    )
      return;
    deletePending.current = true;
    setBusy("delete");
    setDeleteError("");
    try {
      await api.removeMap(map);
      // Clear this map's recovery even if navigation unmounted its editor.
      draftHistory.reset(map.document, map.revision);
      // App updates the library and only navigates if this map is still open.
      onDeleted(map);
    } catch (cause) {
      if (!editorMounted.current) return;
      const stale =
        cause instanceof ApiError &&
        (cause.status === 409 || cause.status === 412);
      if (stale) {
        const message =
          "This map changed since you opened it. Close this dialog and load the latest saved map before deleting it. Your unsaved changes are still here.";
        setDeleteStale(true);
        setDeleteError(message);
        setError(message);
        setConflict(true);
      } else setDeleteError(errorText(cause));
    } finally {
      deletePending.current = false;
      if (editorMounted.current) setBusy("");
    }
  }
  async function restoreRevision(revision: number) {
    if (draftLocked || dirty) return;
    setBusy("restore");
    setReviewError("");
    try {
      adopt(await api.restore(map, revision));
      setHistoryOpen(false);
      setNotice(
        `Revision ${revision} restored as a new saved draft. Your published map is unchanged.`,
      );
    } catch (error) {
      setReviewError(errorText(error));
      if (
        error instanceof ApiError &&
        (error.status === 409 || error.status === 412)
      )
        setError(errorText(error));
      setConflict(
        error instanceof ApiError &&
          (error.status === 409 || error.status === 412),
      );
    } finally {
      setBusy("");
    }
  }
  function closeReview() {
    if (busy) return;
    const token = previewToken.current;
    previewToken.current = null;
    if (token) void api.cancelSource(map.id, token).catch(() => undefined);
    setReview(null);
    setReviewError("");
  }
  function recoverDraft() {
    const recovery = draftHistory.recovery;
    if (!recovery) return;
    if (recovery.stale) {
      setReviewError("");
      setReview({
        kind: "recovery",
        comparison: compareMapDocuments(map.document, recovery.document),
      });
    } else {
      draftHistory.recover();
      setNotice(
        "Recovered changes are in the editor. Review and save them when ready.",
      );
    }
  }
  async function replaceDocument(selected: File | undefined) {
    if (!selected || draftLocked) return;
    setError("");
    try {
      if (selected.size > 1024 * 1024)
        throw new Error("Choose a map JSON file smaller than 1 MB.");
      const next = validateMapDocument(JSON.parse(await selected.text()));
      if (next.id !== map.id)
        throw new Error(
          `This map’s ID is “${map.id}”. The imported document must use the same ID; choose New map → Import JSON document in the library to create a separate map.`,
        );
      if (
        !window.confirm(
          "Replace this draft with the imported map document? Your published version stays unchanged until you publish.",
        )
      )
        return;
      change(next);
      clearSelection();
      setNotice(
        "Document imported into this draft. Save to keep your changes.",
      );
    } catch (error) {
      setError(
        error instanceof SyntaxError
          ? "This file is not valid JSON."
          : errorText(error),
      );
    } finally {
      if (file.current) file.current.value = "";
    }
  }
  async function linkSource() {
    if (!canEdit || busy || dirty || map.source || !sourceUrl.trim()) return;
    setBusy("source");
    setError("");
    setNotice("");
    try {
      adopt(await api.linkSource(map, sourceUrl.trim()), "preserve");
      setSourceUrl("");
      setNotice(
        "Source linked. Your map is unchanged. Choose Refresh from source when you’re ready to import its floor plan into your draft.",
      );
    } catch (error) {
      setError(errorText(error));
      setConflict(
        error instanceof ApiError &&
          (error.status === 409 || error.status === 412),
      );
    } finally {
      setBusy("");
    }
  }
  function changeStand(next: MapStand) {
    change({
      ...draft,
      geometry: {
        ...draft.geometry,
        stands: draft.geometry.stands.map((item) =>
          item.id === next.id ? next : item,
        ),
      },
    });
  }
  function logoDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    const available =
      !draftLocked &&
      !logoUploadPending.current &&
      Array.from(event.dataTransfer?.types ?? []).includes("Files");
    if (event.dataTransfer)
      event.dataTransfer.dropEffect = available ? "copy" : "none";
    setLogoDragging(available);
  }
  function dropLogo(event: DragEvent<HTMLDivElement>) {
    // Suppress browser navigation even for rejected files or a disabled editor.
    event.preventDefault();
    event.stopPropagation();
    logoDragDepth.current = 0;
    setLogoDragging(false);
    if (draftLocked || logoUploadPending.current) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 1) {
      setLogoError("Drop one logo at a time.");
      return;
    }
    void uploadLogo(files[0]);
  }
  async function uploadLogo(selected: File | undefined) {
    if (!selected || !stand || draftLocked || logoUploadPending.current) return;
    // Drag/drop and the picker share one upload, including before state flushes.
    logoUploadPending.current = true;
    logoDragDepth.current = 0;
    setLogoDragging(false);
    setLogoError("");
    setBusy("logo");
    try {
      const contentType = logoFileType(selected);
      if (
        !["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(
          contentType,
        )
      )
        throw new Error("Choose a PNG, JPEG, WebP or SVG image.");
      if (selected.size > 2 * 1024 * 1024)
        throw new Error("Choose a logo no larger than 2 MB.");
      // SVG is validated and normalised on the server before any browser preview.
      if (contentType !== "image/svg+xml") await validateLogoImage(selected);
      const logo = await api.uploadLogo(map.id, selected);
      changeStand({
        ...stand,
        logo,
        labelMode: stand.labelMode ?? "logo-text",
      });
    } catch (error) {
      setLogoError(errorText(error));
    } finally {
      logoUploadPending.current = false;
      setBusy("");
      if (logoFile.current) logoFile.current.value = "";
    }
  }
  function changeLocation(next: MapLocation) {
    const previous = draft.locations.find((item) => item.id === next.id);
    const locations = draft.locations.map((item) =>
      item.id === next.id ? next : item,
    );
    if (
      previous?.label === next.label &&
      previous.x === next.x &&
      previous.y === next.y
    ) {
      change({ ...draft, locations });
      return;
    }
    const labels = { ...draft.labels.entrances };
    if (draft.geometry.entrances.some((entrance) => entrance.id === next.id))
      delete labels[next.id];
    change({
      ...draft,
      locations,
      labels: { ...draft.labels, entrances: labels },
      geometry: {
        ...draft.geometry,
        entrances: draft.geometry.entrances.map((item) =>
          item.id === next.id
            ? {
                ...item,
                label: next.label,
                x: next.x * draft.geometry.width,
                y: next.y * draft.geometry.height,
              }
            : item,
        ),
      },
    });
  }
  function clearSelection() {
    setSelectedStand("");
    setSelectedLocation("");
    setLogoError("");
    previewHandle.current?.clearSelection();
  }
  function selectItem(id: string, focus = true) {
    const nextStand = draft.geometry.stands.find((item) => item.id === id);
    const nextLocation = draft.locations.find((item) => item.id === id);
    setSelectedStand(nextStand?.id || "");
    setSelectedLocation(nextLocation?.id || "");
    setLogoError("");
    if (focus) {
      previewHandle.current?.clearSelection();
      if (
        nextLocation &&
        (showHidden || (!nextLocation.hidden && !nextStand?.hidden))
      )
        previewHandle.current?.focusLocation(nextLocation.id);
    }
  }
  function chooseTab(next: typeof tab) {
    setTab(next);
  }
  const selectLocation = (selected: MapLocation) => {
    selectItem(selected.id, false);
    setTab("locations");
  };
  function toggleVisibility(selected: MapLocation) {
    if (draftLocked) return;
    const selectedBooth = draft.geometry.stands.find(
      (item) => item.id === selected.id,
    );
    if (selectedBooth) {
      // Booth visibility and its navigation marker are deliberately independent.
      changeStand({ ...selectedBooth, hidden: !selectedBooth.hidden });
    } else {
      const selectedPlace = draft.locations.find(
        (item) => item.id === selected.id,
      );
      if (selectedPlace)
        changeLocation({ ...selectedPlace, hidden: !selectedPlace.hidden });
    }
  }
  function usePreviewAngles() {
    const { rotation, pitch } =
      previewHandle.current?.getView() ?? latestView.current;
    change({
      ...draft,
      camera: {
        rotation:
          Math.round((((((rotation + 180) % 360) + 360) % 360) - 180) * 10) /
          10,
        pitch: Math.round(Math.max(0, Math.min(70, pitch)) * 10) / 10,
      },
    });
  }
  return (
    <>
      <button className="em-back-link" onClick={onBack}>
        <ArrowLeft size={16} />
        All maps
      </button>
      <PageHeading
        title={draft.title || "Untitled map"}
        className="em-page-heading em-editor-heading"
        description={
          <>
            <code>{map.id}</code>
            <span className="em-dot-separator">·</span>Saved{" "}
            {updatedDate(map.updatedAt)}
          </>
        }
      >
        <div className="em-actions">
          <button
            type="button"
            className="em-button em-button-plain"
            disabled={!!busy}
            onClick={(event) => {
              historyOpener.current = event.currentTarget;
              setReviewError("");
              setHistoryOpen(true);
            }}
          >
            <History size={16} /> Version history
          </button>
          <Status map={map} />
        </div>
      </PageHeading>
      {draftHistory.recovery && canEdit && (
        <div className="em-recovery-banner" role="status">
          <div>
            <strong>Unsaved changes found</strong>
            <p>
              From{" "}
              {updatedDate(
                new Date(draftHistory.recovery.savedAt).toISOString(),
              )}
              .{" "}
              {draftHistory.recovery.stale
                ? "The saved map has changed since then. Review the recovered changes before using them."
                : "Restore this tab’s unsaved draft or discard it to continue editing."}
            </p>
          </div>
          <div className="em-actions">
            <button
              className="em-button em-button-secondary"
              onClick={(event) => {
                reviewOpener.current = event.currentTarget;
                recoverDraft();
              }}
            >
              {draftHistory.recovery.stale
                ? "Review recovered changes"
                : "Restore unsaved changes"}
            </button>
            <button
              className="em-button em-button-plain"
              onClick={draftHistory.discardRecovery}
            >
              Discard recovered changes
            </button>
          </div>
        </div>
      )}
      {draftHistory.recoveryWarning && (
        <Notice>{draftHistory.recoveryWarning}</Notice>
      )}
      {error && (
        <Notice>
          {error}
          {conflict && (
            <>
              {" "}
              Your edits are still here.{" "}
              <button
                className="em-inline-button"
                disabled={!!busy}
                onClick={() => void action("reload")}
              >
                Load latest saved map
              </button>
            </>
          )}
        </Notice>
      )}
      {notice && <Notice success>{notice}</Notice>}
      {!canEdit && (
        <div className="em-info-line">
          <Eye size={16} />
          You have view-only access to this workspace.
        </div>
      )}
      <div className="em-editor-tabs" role="tablist" aria-label="Map editor">
        {(
          [
            { key: "details", label: "Overview", Icon: Settings2 },
            { key: "locations", label: "Locations", Icon: MapPin },
            { key: "embed", label: "Share", Icon: Code2 },
          ] as const
        ).map(({ key, label, Icon }) => (
          <button
            key={key}
            id={`tab-${key}`}
            role="tab"
            aria-selected={tab === key}
            aria-controls="editor-panel"
            tabIndex={tab === key ? 0 : -1}
            onClick={() => chooseTab(key)}
            onKeyDown={(event) => {
              const keys = ["details", "locations", "embed"] as const;
              const index = keys.indexOf(tab);
              if (
                event.key === "ArrowRight" ||
                event.key === "ArrowLeft" ||
                event.key === "Home" ||
                event.key === "End"
              ) {
                event.preventDefault();
                const next =
                  event.key === "Home"
                    ? keys[0]
                    : event.key === "End"
                      ? keys[keys.length - 1]
                      : keys[
                          (index +
                            (event.key === "ArrowRight"
                              ? 1
                              : keys.length - 1)) %
                            keys.length
                        ];
                chooseTab(next);
                document.getElementById(`tab-${next}`)?.focus();
              }
            }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>
      <div
        className={`em-editor-grid ${tab === "embed" ? "em-editor-grid-embed" : ""}`}
      >
        <section
          id="editor-panel"
          role="tabpanel"
          aria-labelledby={`tab-${tab}`}
          className="em-panel em-editor-panel"
        >
          {tab === "details" && (
            <>
              <div className="em-panel-title">
                <div>
                  <h2>Map details</h2>
                  <p>The name and description shared with your integrations.</p>
                </div>
              </div>
              <fieldset disabled={draftLocked} className="em-field-stack">
                <label>
                  Map name
                  <input
                    maxLength={160}
                    required
                    aria-invalid={!draft.title.trim()}
                    aria-describedby={
                      !draft.title.trim() ? "map-name-error" : undefined
                    }
                    value={draft.title}
                    onChange={(event) =>
                      change({ ...draft, title: event.currentTarget.value })
                    }
                  />
                </label>
                {!draft.title.trim() && (
                  <p id="map-name-error" className="em-field-error">
                    Enter a map name before saving.
                  </p>
                )}
                <label>
                  Description
                  <textarea
                    rows={3}
                    maxLength={1000}
                    value={draft.description}
                    onChange={(event) =>
                      change({
                        ...draft,
                        description: event.currentTarget.value,
                      })
                    }
                  />
                </label>
                <div className="em-section-rule" />
                <h3>Starting view</h3>
                <p className="em-muted">
                  Rotate and tilt the preview, then use its angles as the
                  starting view.
                </p>
                <div className="em-actions">
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    disabled={!previewReady}
                    onClick={usePreviewAngles}
                  >
                    <Eye size={15} />
                    Use preview angles
                  </button>
                </div>
                <details className="em-disclosure">
                  <summary>Fine-tune angles</summary>
                  <div className="em-disclosure-body">
                    <div className="em-form-grid">
                      <label>
                        Rotation <span className="em-field-unit">degrees</span>
                        <input
                          type="number"
                          min={-180}
                          max={180}
                          step={1}
                          value={draft.camera.rotation}
                          onChange={(event) =>
                            change({
                              ...draft,
                              camera: {
                                ...draft.camera,
                                rotation: Math.max(
                                  -180,
                                  Math.min(
                                    180,
                                    Number(event.currentTarget.value),
                                  ),
                                ),
                              },
                            })
                          }
                        />
                      </label>
                      <label>
                        Viewing angle{" "}
                        <span className="em-field-unit">degrees</span>
                        <input
                          type="number"
                          min={0}
                          max={70}
                          step={1}
                          value={draft.camera.pitch}
                          onChange={(event) =>
                            change({
                              ...draft,
                              camera: {
                                ...draft.camera,
                                pitch: Math.max(
                                  0,
                                  Math.min(
                                    70,
                                    Number(event.currentTarget.value),
                                  ),
                                ),
                              },
                            })
                          }
                        />
                      </label>
                    </div>
                  </div>
                </details>
              </fieldset>
              {(map.source || canEdit) && (
                <section
                  className="em-source-card"
                  aria-labelledby="source-heading"
                  aria-busy={busy === "refresh" || busy === "source"}
                >
                  <div className="em-source-heading">
                    <h3 id="source-heading">Source floor plan</h3>
                    <span className="em-source-private">
                      <LockKeyhole size={12} /> Private
                    </span>
                  </div>
                  {map.source ? (
                    <>
                      {map.source.provider === "pdf" ? (
                        <>
                          <a
                            className="em-source-url"
                            href={`/api/admin/maps/${encodeURIComponent(map.id)}/source-file`}
                            download
                          >
                            {map.source.filename}
                          </a>
                          <p className="em-muted">
                            Imported page {map.source.page}. The original PDF is
                            kept privately. To use a revised PDF, import it as a
                            new map.
                          </p>
                        </>
                      ) : (
                        <a
                          href={map.source.url}
                          target="_blank"
                          rel="noreferrer"
                          className="em-source-url"
                        >
                          {map.source.url}
                        </a>
                      )}
                      <p className="em-muted">
                        {map.source.importedAt
                          ? `Last imported ${updatedDate(map.source.importedAt)}.`
                          : "Linked, not imported yet."}{" "}
                        Source details are only shown in this admin workspace.
                      </p>
                      {map.source.provider === "expocad" && (
                        <details className="em-disclosure">
                          <summary>Refresh details</summary>
                          <div className="em-disclosure-body">
                            <p className="em-muted">
                              Refresh updates geometry and exhibitor names from
                              the source, replacing local name edits and
                              removing booths or locations no longer present
                              there. Logos, categories, heights and visibility
                              settings are kept for matching booth IDs. Review
                              what changed before applying it to your saved
                              draft. Publishing remains a separate step.
                            </p>
                          </div>
                        </details>
                      )}
                      {map.source.warnings.length > 0 && (
                        <details className="em-disclosure em-source-warnings">
                          <summary>Import notes</summary>
                          <ul>
                            {map.source.warnings.map((warning, index) => (
                              <li key={index}>{warning}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {canEdit && map.source.provider === "expocad" && (
                        <>
                          <button
                            className="em-button em-button-secondary"
                            disabled={
                              !!busy || dirty || !!draftHistory.recovery
                            }
                            aria-describedby={
                              dirty ? "source-refresh-help" : undefined
                            }
                            onClick={(event) => {
                              reviewOpener.current = event.currentTarget;
                              void action("refresh");
                            }}
                          >
                            <RefreshCw
                              size={15}
                              className={
                                busy === "refresh" ? "em-spin" : undefined
                              }
                            />
                            {busy === "refresh"
                              ? "Refreshing source…"
                              : "Refresh from source"}
                          </button>
                          {dirty && (
                            <p
                              id="source-refresh-help"
                              className="em-field-help"
                            >
                              Save or discard your changes before refreshing the
                              source.
                            </p>
                          )}
                          {busy === "refresh" && (
                            <p className="em-source-progress" role="status">
                              Fetching the latest floor plan…
                            </p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <details className="em-disclosure">
                      <summary>Connect source floor plan</summary>
                      <div className="em-disclosure-body">
                        <form
                          className="em-source-link-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            void linkSource();
                          }}
                        >
                          <p className="em-muted">
                            Link an ExpoCAD floor plan to refresh this map
                            manually. Linking leaves the current map unchanged.
                            The source URL stays private and is never included
                            in exports or embeds.
                          </p>
                          <label>
                            Source floor plan URL
                            <input
                              type="url"
                              value={sourceUrl}
                              maxLength={2048}
                              required
                              placeholder="https://gsma.expocad.com/Events/…/index.html"
                              disabled={
                                !!busy || dirty || !!draftHistory.recovery
                              }
                              onChange={(event) =>
                                setSourceUrl(event.currentTarget.value)
                              }
                              aria-describedby={
                                dirty ? "source-link-help" : undefined
                              }
                            />
                          </label>
                          <button
                            className="em-button em-button-secondary"
                            type="submit"
                            disabled={!!busy || dirty || !sourceUrl.trim()}
                          >
                            {busy === "source" ? (
                              <RefreshCw size={15} className="em-spin" />
                            ) : (
                              <Link2 size={15} />
                            )}
                            {busy === "source"
                              ? "Linking source…"
                              : "Link source"}
                          </button>
                          {dirty && (
                            <p id="source-link-help" className="em-field-help">
                              Save or discard your changes before linking a
                              source.
                            </p>
                          )}
                          {busy === "source" && (
                            <p className="em-source-progress" role="status">
                              Checking the source floor plan…
                            </p>
                          )}
                        </form>
                      </div>
                    </details>
                  )}
                </section>
              )}
              <details className="em-disclosure em-map-file">
                <summary>Map file</summary>
                <div className="em-disclosure-body">
                  <div className="em-panel-title">
                    <div>
                      <h3>Portable map document</h3>
                      <p>
                        {draft.geometry.stands.length} booths ·{" "}
                        {draft.locations.length} locations
                        <br />
                        Import geometry using a versioned JSON map document.
                      </p>
                    </div>
                  </div>
                  <div className="em-actions em-actions-wrap">
                    <button
                      className="em-button em-button-secondary"
                      onClick={() =>
                        download(
                          `${map.id}.json`,
                          JSON.stringify(draft, null, 2),
                        )
                      }
                    >
                      <Download size={15} />
                      Export draft
                    </button>
                    {canEdit && (
                      <button
                        className="em-button em-button-secondary"
                        disabled={draftLocked}
                        onClick={() => file.current?.click()}
                      >
                        <Upload size={15} />
                        Replace from JSON
                      </button>
                    )}
                  </div>
                  <input
                    ref={file}
                    className="em-hidden"
                    type="file"
                    accept=".json,application/json"
                    aria-label="Replace map document from JSON"
                    disabled={draftLocked}
                    onChange={(event) =>
                      void replaceDocument(event.currentTarget.files?.[0])
                    }
                  />
                </div>
              </details>
              {canDelete && (
                <section
                  className="em-map-delete-section"
                  aria-label="Delete map"
                >
                  <div>
                    <h3>Delete this map</h3>
                    <p>
                      Remove the map from this workspace. Only owners can delete
                      maps.
                    </p>
                  </div>
                  <button
                    ref={deleteOpener}
                    type="button"
                    className="em-button em-button-secondary em-map-delete-link"
                    disabled={!!busy || deleteStale}
                    title={
                      deleteStale
                        ? "Load the latest saved map before deleting"
                        : undefined
                    }
                    onClick={() => {
                      if (busy || deletePending.current) return;
                      setDeleteError("");
                      setDeleteOpen(true);
                    }}
                  >
                    <Trash2 size={15} aria-hidden="true" />
                    Delete map
                  </button>
                </section>
              )}
            </>
          )}
          {tab === "locations" && (
            <>
              <div className="em-panel-title em-locations-heading">
                <div>
                  <h2>Booths &amp; places</h2>
                  <p>Manage every location, its appearance and visibility.</p>
                </div>
                <span className="em-count">
                  {draft.geometry.stands.length + places.length}
                </span>
                <AddPlace
                  document={draft}
                  logos={logos}
                  canEdit={canEdit}
                  disabled={draftLocked}
                  onChange={change}
                  onSelect={(id) => {
                    setSelectedStand("");
                    setSelectedLocation(id);
                  }}
                />
              </div>
              <LocationPicker
                stands={draft.geometry.stands}
                locations={draft.locations}
                selectedId={selectedStand || selectedLocation}
                logos={logos}
                disabled={!!busy}
                onSelect={selectItem}
              />
              {stand ? (
                <fieldset className="em-field-stack" disabled={draftLocked}>
                  <div className="em-selection-heading">
                    <span className="em-booth-id">{stand.id}</span>
                    <div>
                      <h3>Booth details</h3>
                      <p>Updates appear in the preview as you type.</p>
                    </div>
                    {stand.hidden && (
                      <span className="em-hidden-status">
                        <EyeOff size={13} aria-hidden="true" />
                        Hidden
                      </span>
                    )}
                  </div>
                  <label>
                    Exhibitor or place name
                    <input
                      maxLength={160}
                      value={stand.name}
                      onChange={(event) =>
                        change(
                          updateStandName(
                            draft,
                            stand,
                            event.currentTarget.value,
                          ),
                        )
                      }
                    />
                  </label>
                  <div className="em-field-group">
                    <label htmlFor="booth-map-label">Map label</label>
                    <input
                      id="booth-map-label"
                      maxLength={160}
                      value={stand.mapLabel ?? ""}
                      placeholder="Use the default label"
                      aria-describedby="booth-map-label-help"
                      onInput={(event) => {
                        const value = event.currentTarget.value;
                        const { mapLabel: _, ...rest } = stand;
                        changeStand(
                          value.trim() ? { ...rest, mapLabel: value } : rest,
                        );
                      }}
                    />
                    <p id="booth-map-label-help" className="em-field-help">
                      Optional shorter text for the map, such as “Pavilion”.
                      Leave blank to use the default label. Cards and search
                      keep the full name; the booth number is shown
                      automatically.
                    </p>
                  </div>
                  <div className="em-field-group">
                    <span
                      id="booth-label-direction-title"
                      className="em-booth-label-direction-title"
                    >
                      Label direction
                    </span>
                    <div
                      className="em-booth-label-direction"
                      role="group"
                      aria-labelledby="booth-label-direction-title"
                      aria-describedby="booth-label-direction-help"
                    >
                      {[
                        {
                          value: -90,
                          text: "Left 90°",
                          label: "Rotate booth label left 90 degrees",
                        },
                        {
                          value: 0,
                          text: "Default",
                          label: "Use default booth label direction",
                        },
                        {
                          value: 90,
                          text: "Right 90°",
                          label: "Rotate booth label right 90 degrees",
                        },
                      ].map(({ value, text, label }) => (
                        <button
                          key={value}
                          type="button"
                          aria-label={label}
                          aria-pressed={(stand.labelRotation ?? 0) === value}
                          onClick={() => {
                            const { labelRotation: _, ...rest } = stand;
                            changeStand(
                              value === 0
                                ? rest
                                : { ...rest, labelRotation: value as -90 | 90 },
                            );
                          }}
                        >
                          {text}
                        </button>
                      ))}
                    </div>
                    <p
                      id="booth-label-direction-help"
                      className="em-field-help"
                    >
                      Turn the label and logo to fit long booths. The booth
                      number stays in its corner. Default uses the imported
                      direction.
                    </p>
                  </div>
                  <div className="em-field-group em-booth-logo-field">
                    <h4>Booth logo</h4>
                    <div
                      className={`em-booth-logo-row${logoDragging && !draftLocked ? " is-dragging" : ""}`}
                      role="group"
                      aria-label="Booth logo upload"
                      aria-describedby="booth-logo-upload-help"
                      aria-disabled={draftLocked}
                      aria-busy={busy === "logo"}
                      onDragEnter={(event) => {
                        if (
                          Array.from(event.dataTransfer?.types ?? []).includes(
                            "Files",
                          )
                        )
                          logoDragDepth.current += 1;
                        logoDragOver(event);
                      }}
                      onDragOver={logoDragOver}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        logoDragDepth.current = Math.max(
                          0,
                          logoDragDepth.current - 1,
                        );
                        if (!logoDragDepth.current) setLogoDragging(false);
                      }}
                      onDrop={dropLogo}
                    >
                      <div className="em-booth-logo-preview">
                        {stand.logo ? (
                          <img
                            src={`/api/admin/maps/${encodeURIComponent(map.id)}/logos/${stand.logo.id}`}
                            alt={`Logo for ${stand.name || stand.id}`}
                            draggable={false}
                            width={stand.logo.width}
                            height={stand.logo.height}
                          />
                        ) : (
                          <ImagePlus size={24} aria-hidden="true" />
                        )}
                      </div>
                      <div className="em-booth-logo-actions">
                        <button
                          type="button"
                          className="em-button em-button-secondary"
                          onClick={() => logoFile.current?.click()}
                        >
                          <Upload size={14} aria-hidden="true" />
                          {busy === "logo"
                            ? "Uploading…"
                            : stand.logo
                              ? "Replace logo"
                              : "Upload logo"}
                        </button>
                        {stand.logo && (
                          <button
                            type="button"
                            className="em-inline-button em-booth-logo-remove"
                            onClick={() => {
                              const { logo: _, labelMode: __, ...rest } = stand;
                              changeStand(rest);
                              setLogoError("");
                            }}
                          >
                            <Trash2 size={13} aria-hidden="true" />
                            Remove logo
                          </button>
                        )}
                      </div>
                    </div>
                    <input
                      ref={logoFile}
                      className="em-hidden"
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                      aria-label="Booth logo file"
                      onChange={(event) =>
                        void uploadLogo(event.currentTarget.files?.[0])
                      }
                    />
                    <p id="booth-logo-upload-help" className="em-field-help">
                      {canEdit && "Drop a logo here or choose a file. "}
                      PNG, JPEG, WebP or SVG · up to 2 MB.
                    </p>
                    {logoError && (
                      <p className="em-booth-logo-error" role="alert">
                        {logoError}
                      </p>
                    )}
                    <label>
                      Booth label
                      <select
                        value={stand.labelMode ?? "text"}
                        aria-describedby="booth-logo-label-help"
                        onChange={(event) =>
                          changeStand({
                            ...stand,
                            labelMode: event.currentTarget.value as
                              "text" | "logo" | "logo-text",
                          })
                        }
                      >
                        <option value="text">Text only</option>
                        <option value="logo" disabled={!stand.logo}>
                          Logo only
                        </option>
                        <option value="logo-text" disabled={!stand.logo}>
                          Logo and text
                        </option>
                      </select>
                    </label>
                    <p id="booth-logo-label-help" className="em-field-help">
                      The booth number stays visible.
                      {!stand.logo &&
                        " Upload a logo to enable the logo layouts."}
                    </p>
                  </div>
                  <label>
                    Category
                    <select
                      value={stand.category}
                      onChange={(event) =>
                        changeStand({
                          ...stand,
                          category: event.currentTarget.value as MapCategory,
                        })
                      }
                    >
                      {categories.map((category) => (
                        <option value={category.value} key={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="em-field-group">
                    <label htmlFor="booth-extrusion-height">
                      <span>
                        Extrusion height{" "}
                        <span className="em-field-unit">Map units</span>
                      </span>
                    </label>
                    <div className="em-input-with-reset">
                      <input
                        id="booth-extrusion-height"
                        aria-label="Extrusion height"
                        aria-describedby="booth-height-help"
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={stand.height ?? ""}
                        placeholder={`Default: ${mapStandHeight(stand.category)}`}
                        onChange={(event) => {
                          const value = event.currentTarget.value;
                          const { height: _, ...rest } = stand;
                          changeStand(
                            value === ""
                              ? rest
                              : {
                                  ...rest,
                                  height: Math.max(
                                    0,
                                    Math.min(100, Number(value)),
                                  ),
                                },
                          );
                        }}
                      />
                      <button
                        type="button"
                        className="em-input-reset"
                        aria-label="Reset extrusion height to default"
                        title={`Reset to category default (${mapStandHeight(stand.category)})`}
                        disabled={stand.height === undefined}
                        onClick={() => {
                          const { height: _, ...rest } = stand;
                          changeStand(rest);
                        }}
                      >
                        <span>RESET</span>
                      </button>
                    </div>
                    <p id="booth-height-help" className="em-field-help">
                      Leave blank for the category default (
                      {mapStandHeight(stand.category)}). Use 0 for a flat booth.
                      Map units are relative drawing units, not metres.
                    </p>
                  </div>
                  <div className="em-field-group">
                    <label className="em-visibility-toggle">
                      <input
                        type="checkbox"
                        checked={!stand.hidden}
                        aria-describedby="booth-visibility-help"
                        onChange={(event) =>
                          changeStand({
                            ...stand,
                            hidden: !event.currentTarget.checked,
                          })
                        }
                      />
                      <span>Show booth on map</span>
                    </label>
                    <p id="booth-visibility-help" className="em-field-help">
                      Hides the booth, its label and associated pins. Wayfinding
                      still avoids its footprint.
                    </p>
                  </div>
                  <details className="em-disclosure em-booth-navigation">
                    <summary>Pin &amp; navigation</summary>
                    <div className="em-disclosure-body">
                      {standLocation ? (
                        <LocationFields
                          key={standLocation.id}
                          location={standLocation}
                          onChange={changeLocation}
                          document={draft}
                          logos={logos}
                          disabled={draftLocked}
                          booth
                          boothHidden={!!stand.hidden}
                        />
                      ) : (
                        <AddBoothLocation
                          document={draft}
                          logos={logos}
                          stand={stand}
                          canEdit={canEdit}
                          disabled={draftLocked}
                          onChange={change}
                          onSelect={(id) => {
                            setSelectedLocation(id);
                            queueMicrotask(() =>
                              document
                                .querySelector<HTMLInputElement>(
                                  "#editor-panel .em-location-fields input",
                                )
                                ?.focus({ preventScroll: true }),
                            );
                          }}
                        />
                      )}
                    </div>
                  </details>
                </fieldset>
              ) : place ? (
                <fieldset className="em-field-stack" disabled={draftLocked}>
                  <div className="em-selection-heading">
                    <MapPin size={20} aria-hidden="true" />
                    <div>
                      <h3>Place details</h3>
                      <p>Updates appear in the preview as you type.</p>
                    </div>
                  </div>
                  <LocationFields
                    key={place.id}
                    location={place}
                    onChange={changeLocation}
                    document={draft}
                    logos={logos}
                    disabled={draftLocked}
                  />
                  <RemovePlace
                    document={draft}
                    logos={logos}
                    location={place}
                    canEdit={canEdit}
                    disabled={draftLocked}
                    onChange={change}
                    onSelect={() => {
                      clearSelection();
                      queueMicrotask(() =>
                        document
                          .querySelector<HTMLButtonElement>(
                            "#editor-panel .em-booth-picker-trigger",
                          )
                          ?.focus({ preventScroll: true }),
                      );
                    }}
                  />
                </fieldset>
              ) : (
                <div className="em-small-empty">
                  <MapPin size={28} />
                  <p>
                    {draft.geometry.stands.length || places.length
                      ? "Choose a booth or place to edit it and see it on the map."
                      : "Import a map document or add a place to get started."}
                  </p>
                </div>
              )}
              <section
                className="em-routing-access-card"
                aria-labelledby="routing-access-heading"
              >
                <div>
                  <h3 id="routing-access-heading">Routing &amp; access</h3>
                  <p className="em-muted">
                    Define where visitors can walk, with barriers and door
                    openings.
                  </p>
                </div>
                {draft.geometry.routing && (
                  <p className="em-field-help">
                    {draft.geometry.routing.walkableAreas.length} walkable areas
                    · {draft.geometry.routing.barriers.length} barriers ·{" "}
                    {draft.geometry.routing.doors.length} doors
                  </p>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="em-button em-button-secondary"
                    disabled={draftLocked}
                    ref={routingOpener}
                    onClick={() => setRoutingOpen(true)}
                  >
                    <MapPin size={15} />
                    Edit walkable routes
                  </button>
                )}
              </section>
            </>
          )}
          {tab === "embed" && (
            <>
              <div className="em-publish-card">
                <div>
                  <Globe size={20} />
                  <h3>
                    {map.publishedRevision !== null
                      ? "Available to your audience"
                      : "Ready when you are"}
                  </h3>
                </div>
                <p>
                  {map.publishedRevision !== null
                    ? `Your embeds use published revision ${map.publishedRevision}. Saving a draft keeps that version in place.`
                    : "Publish your saved map to make it available through the API and script embed."}
                </p>
                {map.publishedAt && (
                  <small>Last published {updatedDate(map.publishedAt)}</small>
                )}
                {canEdit && map.publishedRevision !== null && (
                  <button
                    className="em-inline-button em-unpublish"
                    disabled={!!busy || dirty || !!draftHistory.recovery}
                    title={
                      dirty ? "Save or discard your changes first" : undefined
                    }
                    onClick={() => void action("unpublish")}
                  >
                    {busy === "unpublish" ? "Unpublishing…" : "Unpublish map"}
                  </button>
                )}
              </div>
              <PublicPreviewLink
                mapId={map.id}
                published={map.publishedRevision !== null}
              />
              <EmbedPanel map={map} />
            </>
          )}
        </section>
        <div className="em-preview-column">
          <div className="em-preview-heading">
            <div>
              <Eye size={16} />
              <h2>Live draft preview</h2>
            </div>
            <label className="em-preview-hidden-toggle">
              <input
                type="checkbox"
                role="switch"
                checked={showHidden}
                onChange={(event) => setShowHidden(event.currentTarget.checked)}
              />
              <span>Show hidden</span>
            </label>
          </div>
          <div className="em-map-preview">
            <EventMap
              document={draft}
              logos={logos}
              pins={pins}
              showHidden={showHidden}
              onToggleVisibility={canEdit ? toggleVisibility : undefined}
              visibilityDisabled={draftLocked}
              onSelect={selectLocation}
              onSelectionClear={clearSelection}
              onReady={readyPreview}
              onViewChange={rememberView}
              logoBaseUrl={`/api/admin/maps/${encodeURIComponent(draft.id)}/logos`}
            />
          </div>
          <div className="em-preview-caption">
            <span>Select a booth or place to edit it.</span>
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="em-save-bar">
          <div
            className="em-undo-actions"
            role="group"
            aria-label="Edit history"
          >
            <button
              type="button"
              className="em-button em-button-plain em-history-control"
              disabled={draftLocked || !draftHistory.canUndo}
              onClick={() => {
                draftHistory.undo();
                setNotice("");
              }}
              title="Undo last edit (⌘Z / Ctrl+Z)"
              aria-keyshortcuts="Meta+Z Control+Z"
              aria-label="Undo"
            >
              <Undo2 size={16} />
              <span>Undo</span>
            </button>
            <button
              type="button"
              className="em-button em-button-plain em-history-control"
              disabled={draftLocked || !draftHistory.canRedo}
              onClick={() => {
                draftHistory.redo();
                setNotice("");
              }}
              title="Redo last edit (⌘⇧Z / Ctrl+Shift+Z)"
              aria-keyshortcuts="Meta+Shift+Z Control+Shift+Z"
              aria-label="Redo"
            >
              <Redo2 size={16} />
              <span>Redo</span>
            </button>
          </div>
          <div className="em-actions">
            {dirty && (
              <button
                className="em-button em-button-plain"
                disabled={!!busy}
                onClick={() => {
                  if (window.confirm("Discard your unsaved changes?")) {
                    draftHistory.reset(map.document, map.revision);
                    onDirtyChange(false);
                    setError("");
                  }
                }}
              >
                Discard
              </button>
            )}
            <div className="em-save-control">
              <button
                className="em-button em-button-secondary em-save-button"
                ref={saveButton}
                disabled={!dirty || draftLocked || !draft.title.trim()}
                aria-keyshortcuts="Meta+S Control+S"
                aria-label={busy === "save" ? "Saving…" : "Save draft"}
                aria-describedby={
                  !draft.title.trim()
                    ? `${saveStatusId} ${saveValidationId}`
                    : saveStatusId
                }
                title="Save draft (⌘S on Mac, Ctrl+S on Windows)"
                onClick={() => void action("save")}
              >
                <Save size={16} aria-hidden="true" />
                <span>{busy === "save" ? "Saving…" : "Save draft"}</span>
              </button>
              <span id={saveStatusId} className="em-save-button-status">
                {dirty ? "Unsaved changes" : "All changes saved"}
              </span>
            </div>
            <button
              className="em-button"
              disabled={
                dirty || draftLocked || map.revision === map.publishedRevision
              }
              title={dirty ? "Save your draft before publishing" : undefined}
              onClick={(event) => {
                reviewOpener.current = event.currentTarget;
                void action("publish");
              }}
            >
              <Globe size={16} />
              {busy === "publish" ? "Publishing…" : "Publish"}
            </button>
          </div>
          {!draft.title.trim() && (
            <p
              id={saveValidationId}
              className="em-save-validation em-field-error"
            >
              Add a map name in Overview to save.
            </p>
          )}
        </div>
      )}
      {deleteOpen && canDelete && (
        <DeleteMapDialog
          map={map}
          hasUnsavedChanges={dirty || !!draftHistory.recovery}
          returnFocusTo={deleteOpener.current}
          busy={busy === "delete"}
          error={deleteError}
          stale={deleteStale}
          onConfirm={() => void removeMap()}
          onClose={() => {
            if (deletePending.current) return;
            setDeleteOpen(false);
            setDeleteError("");
          }}
        />
      )}
      {routingOpen && (
        <RoutingEditor
          document={draft}
          logos={logos}
          returnFocusTo={routingOpener.current}
          onApply={(routing) =>
            change({ ...draft, geometry: { ...draft.geometry, routing } })
          }
          onClose={() => setRoutingOpen(false)}
        />
      )}
      {historyOpen && (
        <MapHistoryDialog
          returnFocusTo={historyOpener.current}
          mapId={map.id}
          currentRevision={map.revision}
          publishedRevision={map.publishedRevision}
          canRestore={canEdit && !dirty && !draftHistory.recovery}
          busy={busy === "restore"}
          error={reviewError}
          onRestore={restoreRevision}
          onClose={() => {
            setHistoryOpen(false);
            setReviewError("");
          }}
        />
      )}
      {review && (
        <MapChangeReview
          returnFocusTo={reviewOpener.current}
          kind={review.kind}
          comparison={review.comparison}
          busy={!!busy}
          error={reviewError}
          onClose={closeReview}
          onConfirm={() => {
            if (review.kind === "recovery") {
              draftHistory.recover();
              setReview(null);
              setNotice(
                "Recovered changes are in the editor. Review and save them when ready.",
              );
            } else
              void action(
                review.kind === "source" ? "refresh" : "publish",
                true,
              );
          }}
        />
      )}
    </>
  );
}

function EmbedPanel({ map }: { map: AdminMap }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const code = `<div id="event-map" style="width: 100%; height: 600px;"></div>\n<script src="${window.location.origin}/embed/v1.js"\n  data-map="${map.id}"\n  data-target="#event-map" defer></script>`;
  const endpoint = `${window.location.origin}/api/v1/maps/${encodeURIComponent(map.id)}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setError("");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(
        "Copy is unavailable in this browser. Select the code below and copy it manually.",
      );
    }
  }
  return (
    <>
      <div className="em-panel-title">
        <div>
          <h2>Put your map anywhere</h2>
          <p>
            Add this snippet to your page. The map fills its container and stays
            up to date when you publish.
          </p>
        </div>
      </div>
      <label>
        Script embed
        <textarea
          className="em-code"
          readOnly
          spellcheck={false}
          rows={6}
          value={code}
          onFocus={(event) => event.currentTarget.select()}
        />
      </label>
      <div className="em-actions em-actions-wrap">
        <button
          className="em-button em-button-secondary"
          onClick={() => void copy()}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copied" : "Copy embed"}
        </button>
        <button
          className="em-button em-button-secondary"
          onClick={() =>
            download(
              `${map.id}-embed.html`,
              `<!doctype html>\n<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Gem</title></head><body style="margin:0">\n${code}\n</body></html>`,
              "text/html",
            )
          }
        >
          <Download size={16} />
          Download example
        </button>
      </div>
      {error && <Notice>{error}</Notice>}
      <details className="em-disclosure em-api-endpoints">
        <summary>API endpoints</summary>
        <div className="em-disclosure-body">
          <p className="em-muted">
            Published map data is available without authentication. Drafts stay
            private.
          </p>
          <label className="em-api-label">
            Map document
            <input
              readOnly
              className="em-code-input"
              value={endpoint}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
          <label className="em-api-label">
            Locations
            <input
              readOnly
              className="em-code-input"
              value={`${endpoint}/locations`}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
        </div>
      </details>
      <div className="em-info-box em-embed-tip">
        <Code2 size={19} />
        <span>
          Explore the API and try camera, pin and route controls in{" "}
          <a href={`#/documentation?map=${encodeURIComponent(map.id)}`}>
            Documentation &amp; playground
          </a>
          .
        </span>
      </div>
    </>
  );
}
